import React, { useMemo, useState } from 'react';
import { Accordion, Box, Button, Flex, Modal, ModalContent, ModalOverlay, Text } from '@chakra-ui/react';
import type { MarketplaceOpportunityListItemDto } from '@foodfluence/shared';
import {
  formatMarketplaceOrderDateLong,
  listMarketplaceDeliverables,
  marketplaceOrderBundleTitle,
  marketplaceOrderInspirationLinks,
} from '../utils/marketplaceOrderUtils';
import { ff } from '../../../theme';
import { MarketplaceOrderBriefAccordionItems } from './MarketplaceOrderBriefAccordion';
import { MarketplaceOrderStickyHeader } from './MarketplaceOrderStickyHeader';
import { ApplyToOpportunitySheet } from './ApplyToOpportunitySheet';

export interface OpportunityDetailSheetProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: MarketplaceOpportunityListItemDto;
}

export function OpportunityDetailSheet({ isOpen, onClose, opportunity }: OpportunityDetailSheetProps) {
  const [applyOpen, setApplyOpen] = useState(false);
  const [applied, setApplied] = useState(false);

  const avatarLetter = (opportunity.brandName?.trim()?.[0] ?? 'B').toUpperCase();

  const deliverables = useMemo(
    () => listMarketplaceDeliverables(opportunity.deliverables ?? null),
    [opportunity.deliverables]
  );

  const briefModel = useMemo(() => {
    const brief = opportunity.orderBrief as any;
    return {
      deliverables,
      bundleTitle: marketplaceOrderBundleTitle(deliverables),
      cash: opportunity.cash ?? 0,
      narrative: brief?.narrative ?? null,
      contentBriefFile: brief?.requirements?.contentBriefFile ?? null,
      fulfillmentType: brief?.logistics?.fulfillmentType ?? null,
      idealTimelineDisplay: formatMarketplaceOrderDateLong(
        brief?.logistics?.contentDeliveryTargetDate ?? null
      ),
      inspirationLinks: marketplaceOrderInspirationLinks(brief, opportunity.deliverables),
      otherNotes: brief?.requirements?.otherNotes ?? null,
    };
  }, [opportunity, deliverables]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} motionPreset="slideInBottom" blockScrollOnMount>
        <ModalOverlay />
        <ModalContent
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          top={0}
          mb={0}
          mt={0}
          mx={0}
          maxW="100%"
          w="100%"
          h="100dvh"
          maxH="100dvh"
          borderRadius={0}
          bg="white"
          boxShadow="none"
          p={0}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <MarketplaceOrderStickyHeader
            onBack={onClose}
            avatarLetter={avatarLetter}
            brandName={opportunity.brandName ?? 'Brand'}
          />

          <Box flex={1} minH={0} overflowY="auto" pb="104px">
            <Accordion allowMultiple defaultIndex={[0, 1, 2, 3, 4]}>
              <MarketplaceOrderBriefAccordionItems model={briefModel} />
            </Accordion>
          </Box>

          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            bg="white"
            borderTop="1px solid"
            borderColor={ff.line}
            px={5}
            pt={3}
            pb="max(16px, env(safe-area-inset-bottom))"
            fontFamily="display"
          >
            {applied ? (
              <Flex h="56px" borderRadius="button" bg={ff.grey} align="center" justify="center" gap={2}>
                <Text fontSize="15px" fontWeight="600" color={ff.text2}>
                  Application submitted
                </Text>
              </Flex>
            ) : (
              <Button variant="lime" w="100%" onClick={() => setApplyOpen(true)}>
                Send request
              </Button>
            )}
          </Box>
        </ModalContent>
      </Modal>

      <ApplyToOpportunitySheet
        isOpen={applyOpen}
        onClose={() => setApplyOpen(false)}
        opportunity={opportunity}
        onSuccess={() => {
          setApplied(true);
          setApplyOpen(false);
        }}
      />
    </>
  );
}
